/**
 * Web Audio player for the market-session chimes.
 *
 * Two things here are load-bearing and worth understanding before changing them:
 *
 * 1. AUTOPLAY POLICY. Chrome/Edge/Safari refuse to start audio until the user has
 *    interacted with the page. An AudioContext created before any gesture lands in
 *    "suspended" state and stays there. So we never claim sound is armed until we
 *    have actually resumed a context off a real click — see `unlock()`, and the
 *    honest armed/blocked state the toggle renders from it.
 *
 * 2. SCHEDULE ON THE AUDIO CLOCK, NOT setTimeout. Browsers throttle timers in
 *    background tabs (typically to >=1/second, sometimes far worse), which would
 *    turn the ten-second countdown into an arrhythmic mess exactly when the tab is
 *    most likely to be buried behind TWS. AudioContext.currentTime runs on the audio
 *    thread and is immune to that, so we pre-schedule the whole countdown plus the
 *    closing bell in ONE call ~11 seconds ahead and let the audio thread keep time.
 *
 * 3. NEVER TRUST ONE BAD DOWNLOAD. Assets are fetched with `force-cache`, which replays
 *    whatever the browser stored for the URL - including a bad copy. On 2026-09-17 a bad
 *    stored copy of the bells kept the chimes unarmed through clicks, a site autoplay
 *    grant and a full browser restart; only clearing the browser cache by hand fixed it.
 *    So any failure is retried straight from the network (which also overwrites the
 *    stored copy), sounds that still fail are retried in the background, and the player
 *    can say WHY it is not armed - see `diagnose()`.
 *
 * The sounds themselves are static assets rendered by scripts/render_chimes.py. To
 * replace them with licensed recordings, overwrite the files in public/sounds/ with
 * the same names — no code change needed.
 */

export type ChimeName = "open-bell" | "close-bell" | "warn-5min" | "warn-2min" | "pip";

/**
 * BUMP THIS whenever public/sounds/*.wav are re-rendered.
 *
 * The files live at fixed paths and are fetched with `cache: "force-cache"`, which
 * tells the browser to reuse a cached copy regardless of freshness. Without a version
 * in the URL, a browser that has already heard the old audio keeps playing it forever
 * and a redeploy silently does nothing — the failure is invisible, because everything
 * about the app looks correct and the sound is simply the wrong one.
 *
 * Changing this constant changes the URL, which is what actually busts the cache.
 *
 * v2 = 2026-08-19, open and close restated three times.
 * v3 = 2026-08-19, wider pause between the opening chime's three statements.
 * v4 = 2026-08-28, open and close replaced with a REAL bell recording
 *      (see scripts/install_real_bell.py); countdown pips now descend.
 * v5 = 2026-08-28, longer bells - open 10.1s, close 16.9s.
 * v6 = 2026-08-28, close is now the SAME bell as the open (no pitch drop,
 *      matched level), simply rung longer - 10.1s vs 15.1s.
 */
export const CHIME_ASSET_VERSION = "6";

const asset = (name: string): string => `/sounds/${name}.wav?v=${CHIME_ASSET_VERSION}`;

export const CHIME_FILES: Record<ChimeName, string> = {
  "open-bell": asset("open-bell"),
  "close-bell": asset("close-bell"),
  "warn-5min": asset("warn-5min"),
  "warn-2min": asset("warn-2min"),
  pip: asset("pip"),
};

/** Plain names for the control panel, which must say WHICH sound failed to load. */
export const CHIME_LABELS: Record<ChimeName, string> = {
  "open-bell": "opening bell",
  "close-bell": "closing bell",
  "warn-5min": "five-minute warning",
  "warn-2min": "two-minute warning",
  pip: "countdown pip",
};

/**
 * Why the chimes are or are not armed.
 *
 * Each reason has a different remedy, so the panel must never collapse them into one
 * message. It used to: every failure read "click anywhere", which sent the operator after
 * a fix that could not work when the real problem was a sound file that would not load.
 */
export type ChimeHealthKind =
  | "ready" //          context running, every sound decoded
  | "starting" //       still coming up
  | "needs-click" //    the browser is waiting for a first interaction with the page
  | "audio-stuck" //    allowed to play, but the audio output will not start
  | "sounds-missing" // audio runs, but at least one sound failed to load (retrying)
  | "unsupported"; //   no Web Audio in this browser

export interface ChimeHealth {
  kind: ChimeHealthKind;
  /** Sounds whose last load attempt failed. They are retried in the background. */
  missing: ChimeName[];
}

export interface ChimeTiming {
  /** A resume() that never settles must not freeze arming: stop waiting after this. */
  resumeTimeoutMs: number;
  /** Likewise a stalled download. */
  fetchTimeoutMs: number;
  /** How often a sound that failed to load is re-fetched with no interaction at all. */
  loadRetryMs: number;
}

export const CHIME_TIMING: ChimeTiming = {
  resumeTimeoutMs: 3000,
  fetchTimeoutMs: 20000,
  loadRetryMs: 60000,
};

const isRunning = (ctx: BaseAudioContext): boolean => ctx.state === "running";

/** Per-sound trim so one master volume gives a sensible balance between them. */
const RELATIVE_GAIN: Record<ChimeName, number> = {
  "open-bell": 1.0,
  "close-bell": 1.0,
  "warn-5min": 0.85,
  "warn-2min": 0.9,
  pip: 0.8,
};

export interface CountdownShape {
  /** Number of pips before the bell. */
  pips: number;
  /** Playback-rate (pitch) at the first and last pip. */
  rateFrom: number;
  rateTo: number;
  /** Gain multiplier at the first and last pip. */
  gainFrom: number;
  gainTo: number;
}

/**
 * The countdown DESCENDS in pitch: each pip is lower than the last, so the ten
 * seconds audibly run down towards the bell rather than winding up.
 *
 * Volume still rises across the sequence. That is deliberate and not a contradiction:
 * falling pitch is what reads as "time running out", while rising level keeps the last
 * pips from disappearing under whatever else is going on right before the close. If
 * both fell, the final second - the one that actually matters - would be the quietest.
 *
 * Note playbackRate below 1.0 also lengthens each pip slightly, which suits the
 * descent: the clicks get lower AND a touch heavier as they go.
 */
export const COUNTDOWN: CountdownShape = {
  pips: 10,
  rateFrom: 1.35,
  rateTo: 0.8,
  gainFrom: 0.55,
  gainTo: 1.0,
};

export interface PlannedPip {
  /** Instant the pip should sound. */
  atMs: number;
  gain: number;
  /** Playback rate; doubles as the pitch shift, so the pips climb as time runs out. */
  rate: number;
}

/**
 * The countdown as pure data: which pips land when, at what gain and pitch.
 *
 * Pips already in the past are dropped, so arming the sound at 15:59:56 still catches
 * the remaining four and the bell rather than firing a burst of late pips at once.
 * Separated from the audio layer so the arithmetic is testable without a browser.
 */
export function countdownPlan(
  closeMs: number,
  nowMs: number,
  shape: CountdownShape = COUNTDOWN,
): PlannedPip[] {
  const out: PlannedPip[] = [];
  for (let i = 0; i < shape.pips; i += 1) {
    const secondsBefore = shape.pips - i; // 10, 9, ... 1
    const atMs = closeMs - secondsBefore * 1000;
    if (atMs <= nowMs) continue;
    const k = shape.pips > 1 ? i / (shape.pips - 1) : 1;
    out.push({
      atMs,
      gain: shape.gainFrom + (shape.gainTo - shape.gainFrom) * k,
      rate: shape.rateFrom + (shape.rateTo - shape.rateFrom) * k,
    });
  }
  return out;
}

export class ChimePlayer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers = new Map<ChimeName, AudioBuffer>();
  private loading: Promise<void> | null = null;
  private volume = 0.7;
  /** Sources scheduled but not yet finished — so a mid-countdown "mute" can stop them. */
  private live = new Set<AudioBufferSourceNode>();
  private keepAlive: { osc: OscillatorNode; gain: GainNode } | null = null;
  /** Sounds whose last load attempt failed, with the reason. */
  private failed = new Map<ChimeName, string>();
  private lastLoadAttemptMs = 0;
  /** The context has run at least once on this page, so the browser has granted audio. */
  private everRan = false;
  /** The last resume attempt timed out although the page was allowed to play sound. */
  private audioStuck = false;

  constructor(private readonly timing: ChimeTiming = CHIME_TIMING) {}

  /**
   * Hold the tab open for business: a continuous, inaudible tone.
   *
   * Chrome aggressively throttles and eventually FREEZES background tabs — a frozen
   * tab runs no JavaScript at all, so nothing queues the next cue and the chime simply
   * never happens. Tabs that are producing audio output are exempt from that. A silent
   * context does not count; the output has to be genuinely non-zero.
   *
   * So we run a 30 Hz tone at about -66 dBFS. That is below what a laptop speaker can
   * reproduce and far below audibility at that frequency, but it is non-zero, which is
   * what keeps the tab alive.
   *
   * Routed straight to the destination rather than through the master gain ON PURPOSE:
   * if it went through the volume control, setting volume to zero would silence it and
   * silently reintroduce the freezing problem.
   */
  startKeepAlive(): void {
    if (!this.ctx || this.keepAlive) return;
    try {
      const osc = this.ctx.createOscillator();
      osc.frequency.value = 30;
      const gain = this.ctx.createGain();
      gain.gain.value = 0.0005;
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      this.keepAlive = { osc, gain };
    } catch {
      /* non-fatal — we simply lose the anti-freeze protection */
    }
  }

  stopKeepAlive(): void {
    if (!this.keepAlive) return;
    try {
      this.keepAlive.osc.stop();
      this.keepAlive.osc.disconnect();
      this.keepAlive.gain.disconnect();
    } catch {
      /* already torn down */
    }
    this.keepAlive = null;
  }

  /** True once a real user gesture has resumed the context and buffers are decoded. */
  get ready(): boolean {
    return this.ctx?.state === "running" && this.buffers.size === Object.keys(CHIME_FILES).length;
  }

  /** True when we have a context but the browser is still refusing to run it. */
  get blocked(): boolean {
    return this.ctx != null && this.ctx.state !== "running";
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume;
  }

  /**
   * Resume the audio context and decode the assets. MUST be called from within a
   * user-gesture handler (a click) the first time, or the browser will keep the
   * context suspended and every scheduled sound will be silently dropped.
   *
   * `fromGesture`: the caller is a click or keypress handler. Only then (or once audio
   * has run on this page) does a resume that times out mean the audio output is stuck
   * rather than that the browser wants a click. `navigator.userActivation` cannot stand
   * in for this: Edge was measured reporting `hasBeenActive: true` on a fresh load while
   * still refusing to start audio, which made the panel blame the speakers for a
   * missing click.
   */
  async unlock(fromGesture = false): Promise<boolean> {
    try {
      if (!this.ctx) {
        const Ctor: typeof AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return false;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.volume;
        this.master.connect(this.ctx.destination);
      }
      const running = await this.resumeWithin(fromGesture);
      // Loaded whether or not audio is running yet. Decoding does not need a running
      // context, and a sound that will not load must be reported as that - not as
      // "click to enable" - even before the first click.
      await this.load();
      if (running) this.startKeepAlive();
      return running;
    } catch {
      return false;
    }
  }

  /**
   * Resume the context, but stop waiting after `timing.resumeTimeoutMs`.
   *
   * Before a user gesture (and with no site autoplay grant) Chromium leaves resume()'s
   * promise pending, possibly for good, and so does a context whose audio output never
   * starts. Awaited unbounded, that froze every arming path behind its in-flight guard.
   * A timeout counts as `audioStuck` only inside a gesture handler or once the context
   * has run before; otherwise it is just the browser waiting for a click.
   */
  private async resumeWithin(fromGesture: boolean): Promise<boolean> {
    const ctx = this.ctx;
    if (!ctx) return false;
    if (!isRunning(ctx)) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          ctx.resume(),
          new Promise<void>((resolve) => {
            timer = setTimeout(resolve, this.timing.resumeTimeoutMs);
          }),
        ]);
      } catch {
        /* judged by the state below */
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    }
    const running = isRunning(ctx);
    if (running) this.everRan = true;
    this.audioStuck = !running && (this.everRan || fromGesture);
    return running;
  }

  /** Fetch + decode every asset not yet loaded. Safe to call repeatedly. */
  private load(): Promise<void> {
    if (this.loading) return this.loading;
    const ctx = this.ctx;
    if (!ctx) return Promise.resolve();
    this.lastLoadAttemptMs = Date.now();

    this.loading = (async () => {
      const names = Object.keys(CHIME_FILES) as ChimeName[];
      await Promise.all(
        names.map(async (name) => {
          if (this.buffers.has(name)) return;
          try {
            this.buffers.set(name, await this.fetchAndDecode(ctx, name, "force-cache"));
          } catch (stored) {
            // force-cache replays whatever the browser stored, bad copy included, and that
            // copy fails identically on every load until the cache is cleared by hand. Go
            // to the network instead, which also replaces the stored copy.
            try {
              this.buffers.set(name, await this.fetchAndDecode(ctx, name, "reload"));
            } catch (fresh) {
              // A missing or undecodable asset must never break the dashboard: the chime
              // just does not sound, the panel names it, and it is retried in the background.
              this.failed.set(name, fresh instanceof Error ? fresh.message : String(fresh));
              console.warn("[chimes] could not load", name, stored, fresh);
              return;
            }
          }
          this.failed.delete(name);
        }),
      );
    })();

    // Allow a retry later if anything is still missing.
    this.loading.finally(() => {
      if (this.buffers.size !== Object.keys(CHIME_FILES).length) this.loading = null;
    });
    return this.loading;
  }

  private async fetchAndDecode(
    ctx: BaseAudioContext,
    name: ChimeName,
    cache: RequestCache,
  ): Promise<AudioBuffer> {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), this.timing.fetchTimeoutMs);
    try {
      const res = await fetch(CHIME_FILES[name], { cache, signal: abort.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${CHIME_FILES[name]}`);
      const bytes = await res.arrayBuffer();
      // A cut-off download can still decode, into a bell that stops short. Refuse it.
      const declared = Number(res.headers.get("content-length"));
      if (!res.headers.get("content-encoding") && declared > 0 && bytes.byteLength !== declared) {
        throw new Error(`truncated: ${bytes.byteLength} of ${declared} bytes`);
      }
      return await ctx.decodeAudioData(bytes);
    } finally {
      clearTimeout(timer);
    }
  }

  /** Why the chimes are or are not armed, for the control panel. */
  diagnose(): ChimeHealth {
    const missing = (Object.keys(CHIME_FILES) as ChimeName[]).filter((n) => this.failed.has(n));
    const hasWebAudio =
      typeof window !== "undefined" && Boolean(window.AudioContext || (window as any).webkitAudioContext);
    if (!hasWebAudio) return { kind: "unsupported", missing };
    if (!this.ctx) return { kind: "starting", missing };
    if (!isRunning(this.ctx)) {
      return { kind: this.audioStuck ? "audio-stuck" : "needs-click", missing };
    }
    if (missing.length) return { kind: "sounds-missing", missing };
    return { kind: this.ready ? "ready" : "starting", missing };
  }

  /** Convert an epoch-ms instant into this context's audio-clock time. */
  private ctxTimeFor(epochMs: number): number {
    const ctx = this.ctx!;
    return Math.max(ctx.currentTime, ctx.currentTime + (epochMs - Date.now()) / 1000);
  }

  private scheduleBuffer(name: ChimeName, atCtxTime: number, gain = 1, rate = 1): void {
    const ctx = this.ctx;
    const buf = this.buffers.get(name);
    if (!ctx || !this.master || !buf) return;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;

    const g = ctx.createGain();
    g.gain.value = gain * RELATIVE_GAIN[name];

    src.connect(g);
    g.connect(this.master);
    src.start(atCtxTime);
    this.live.add(src);
    src.onended = () => {
      this.live.delete(src);
      try {
        src.disconnect();
        g.disconnect();
      } catch {
        /* already torn down */
      }
    };
  }

  /**
   * Stop everything scheduled or sounding. Needed because the countdown is queued on
   * the audio clock up to a minute ahead — without this, turning the sound off at
   * 15:59:45 would still fire the pips and the bell.
   */
  cancelScheduled(): void {
    this.live.forEach((src) => {
      try {
        src.onended = null;
        src.stop();
        src.disconnect();
      } catch {
        /* already started/stopped; nothing to do */
      }
    });
    this.live.clear();
  }

  /** Suspend the context (releases the audio device when chimes are off). */
  async suspend(): Promise<void> {
    this.cancelScheduled();
    this.stopKeepAlive();
    try {
      if (this.ctx && this.ctx.state === "running") await this.ctx.suspend();
    } catch {
      /* non-fatal */
    }
  }

  /**
   * Re-resume after the OS or browser suspended the context (laptop sleep, tab
   * discard). Unlike unlock() this does not need a user gesture, because the context
   * was already unlocked once — but it can still fail, so callers must check.
   *
   * `wasSuspended` reports whether the context had actually stopped, which callers
   * MUST act on if they have queued anything ahead of time. Scheduling is done against
   * AudioContext.currentTime, and that clock does not advance while suspended — so a
   * sound queued for 90 seconds out and interrupted by a laptop sleep would fire 90
   * seconds of *running* time later, which can be far in the wall-clock future. The
   * only safe response is to drop what was queued and re-derive it from the real time.
   *
   * Also the background retry for sounds that failed to load, at most once per
   * `timing.loadRetryMs`: no click can fix a bad download, so recovery must not wait for one.
   */
  async resumeIfNeeded(): Promise<{ ok: boolean; wasSuspended: boolean }> {
    try {
      if (!this.ctx) return { ok: false, wasSuspended: false };
      if (this.failed.size && Date.now() - this.lastLoadAttemptMs >= this.timing.loadRetryMs) {
        void this.load();
      }
      if (isRunning(this.ctx)) {
        this.audioStuck = false;
        this.startKeepAlive();
        return { ok: true, wasSuspended: false };
      }
      // Never started on this page: that is the browser waiting for a first click, which
      // only a gesture handler can satisfy. Calling resume() here would just log a warning.
      if (!this.everRan) return { ok: false, wasSuspended: false };
      const ok = await this.resumeWithin(false);
      if (ok) this.startKeepAlive();
      return { ok, wasSuspended: true };
    } catch {
      return { ok: false, wasSuspended: true };
    }
  }

  /** Play one sound immediately. */
  play(name: ChimeName, gain = 1): void {
    if (!this.ctx) return;
    this.scheduleBuffer(name, this.ctx.currentTime, gain);
  }

  /** Play one sound at a wall-clock instant (epoch ms). */
  playAt(name: ChimeName, epochMs: number, gain = 1): void {
    if (!this.ctx) return;
    this.scheduleBuffer(name, this.ctxTimeFor(epochMs), gain);
  }

  /**
   * Schedule the final-seconds countdown and the closing bell in one shot.
   *
   * `closeMs` is the instant of the close itself; the pips land on the whole seconds
   * before it. Pips already in the past are skipped, so arming the sound at 15:59:56
   * still catches the remaining four and the bell.
   *
   * Returns the number of pips actually scheduled.
   */
  scheduleCountdown(closeMs: number, shape: CountdownShape = COUNTDOWN): number {
    if (!this.ctx) return 0;
    const now = Date.now();

    const plan = countdownPlan(closeMs, now, shape);
    plan.forEach((p) => this.scheduleBuffer("pip", this.ctxTimeFor(p.atMs), p.gain, p.rate));

    if (closeMs > now) this.scheduleBuffer("close-bell", this.ctxTimeFor(closeMs));
    return plan.length;
  }

  /** Demo of the close sequence, compressed, for the "preview" control. */
  previewCloseSequence(): void {
    if (!this.ctx) return;
    const t = Date.now();
    this.playAt("warn-5min", t + 100);
    this.playAt("warn-2min", t + 3600);
    this.scheduleCountdown(t + 7600 + COUNTDOWN.pips * 1000);
  }
}

export const chimePlayer = new ChimePlayer();
